import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import styles from "./Mobileemail.module.css";
import {
  Add24Regular,
  Archive24Regular,
  ArrowClockwise24Regular,
  ArrowForward24Regular,
  ArrowLeft24Regular,
  ArrowReply24Regular,
  ArrowReplyAll24Regular,
  Attach24Regular,
  Checkmark24Regular,
  ChevronDown16Regular,
  Delete24Regular,
  Dismiss24Regular,
  Drafts24Regular,
  Flag16Filled,
  Flag24Filled,
  Flag24Regular,
  Folder24Regular,
  FolderArrowRight24Regular,
  MailInbox24Regular,
  Mail24Filled,
  Mail24Regular,
  MailRead24Regular,
  MailUnread24Regular,
  Grid24Regular,
  Navigation24Regular,
  Pin16Filled,
  Search24Regular,
  Send24Regular,
  Alert24Regular,
  ChevronDown24Regular,
  ChevronUp24Regular,
  Edit24Regular,
  History24Regular,
  Image24Regular,
  Info24Regular,
  MoreHorizontal24Regular,
  People24Filled,
  People24Regular,
  Prohibited24Regular,
  TextBold24Regular,
  TextBulletList24Regular,
  TextFont24Regular,
  TextItalic24Regular,
  TextUnderline24Regular,
  ShieldError24Regular,
  Attach16Regular,
} from "@fluentui/react-icons";
import * as mailApi from "../../Email/Api/emailApi";
import useAuth from "../../../hooks/useAuth";
import baseUrl from "../../../constants/baseurl";

/* =====================================================================
   Types
   ===================================================================== */
type Att = { id?: string; name: string; size: number; type: string; file?: File };

type Mail = {
  id: string;
  subject: string;
  from: string;
  fromName: string | null;
  to: string[];
  toNames: string[];
  cc: string[];
  ccNames: string[];
  date: string;
  snippet: string;
  body: string;
  isRead: boolean;
  isFlagged: boolean;
  attachments: Att[];
  folder: string;
  pinned: boolean;
  external: boolean;
  focused: boolean;
  categories: string[];
  snoozeUntil: number | null;
};

type Draft = {
  key: string;
  draftId: string | null;
  mode: "new" | "reply" | "replyAll" | "forward";
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  html: string;
  attachments: Att[];
  sourceId?: string;
  attachFrom?: string;
};

type FilterKey = "all" | "unread" | "flagged" | "attachments";
type SortKey = "dateDesc" | "dateAsc";
type Tab = "mail" | "search" | "people" | "me";
type Toast = { id: number; msg: string; undo: (() => void) | null } | null;

const PAGE_SIZE = 40;

const MAIL_FOLDERS: { id: string; label: string; icon: React.ReactElement }[] = [
  { id: "inbox", label: "Inbox", icon: <MailInbox24Regular /> },
  { id: "drafts", label: "Drafts", icon: <Drafts24Regular /> },
  { id: "sent", label: "Sent", icon: <Send24Regular /> },
  { id: "archive", label: "Archive", icon: <Archive24Regular /> },
  { id: "junk", label: "Junk", icon: <ShieldError24Regular /> },
  { id: "trash", label: "Deleted", icon: <Delete24Regular /> },
];

/* =====================================================================
   Helpers
   ===================================================================== */
const textOf = (html: string) =>
  html
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();

const nameFromAddr = (a: string) =>
  (a.split("@")[0] ?? a).replace(/[._]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const isEmail = (s: string) => /^[^\s@<>,;]+@[^\s@<>,;]+\.[^\s@<>,;]+$/.test(s);
const sizeLabel = (n: number) =>
  n < 1024 ? `${n} B` : n < 1048576 ? `${Math.round(n / 1024)} KB` : `${(n / 1048576).toFixed(1)} MB`;

const initialsOf = (name: string) => {
  const parts = name.replace(/[^\p{L}\p{N}\s]/gu, " ").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const AVATAR_COLORS = ["#0f6cbd", "#7a3fb5", "#1e7a3c", "#c2570c", "#0b6a6a", "#b8791a", "#c4314b", "#2b5fd9", "#6b4fbb", "#0a7f9e"];
const colorOf = (s: string) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
const listTime = (iso: string) => {
  const d = new Date(iso);
  const now = new Date();
  if (startOfDay(d) === startOfDay(now)) return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  if (startOfDay(d) === startOfDay(now) - 86400000) return "Yesterday";
  if (now.getTime() - d.getTime() < 6 * 86400000) return d.toLocaleDateString("en-GB", { weekday: "short" });
  return d.getFullYear() === now.getFullYear()
    ? d.toLocaleDateString("en-GB", { day: "numeric", month: "short" })
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" });
};
const fullTime = (iso: string) =>
  new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

/** Strips scripts, handlers and (unless allowed) remote images from untrusted HTML. */
const sanitize = (html: string, allowRemote: boolean): { html: string; blocked: number } => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("script,style,iframe,object,embed,link,meta,form,base,svg").forEach((n) => n.remove());
  let blocked = 0;
  doc.querySelectorAll("*").forEach((el) => {
    Array.from(el.attributes).forEach((a) => {
      const n = a.name.toLowerCase();
      const v = a.value.trim().toLowerCase();
      if (n.startsWith("on")) el.removeAttribute(a.name);
      else if (["href", "src", "action", "formaction", "xlink:href"].includes(n) && v.startsWith("javascript:")) el.removeAttribute(a.name);
      else if (n === "style" && /url\s*\(/i.test(v) && !allowRemote) {
        el.removeAttribute(a.name);
        blocked++;
      }
    });
  });
  doc.querySelectorAll("a").forEach((a) => {
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer nofollow");
  });
  doc.querySelectorAll("img").forEach((img) => {
    const s = img.getAttribute("src") || "";
    if (/^(https?:)?\/\//i.test(s) && !allowRemote) {
      blocked++;
      img.removeAttribute("src");
      img.removeAttribute("srcset");
      img.setAttribute("style", "display:inline-block;min-width:90px;min-height:22px;background:#f3f2f1;border:1px dashed #c8c6c4");
    }
  });
  return { html: doc.body.innerHTML, blocked };
};

const quoteOf = (m: Mail) =>
  `<br><br><div>On ${fullTime(m.date)}, ${(m.fromName ?? m.from).replace(/</g, "&lt;")} &lt;${m.from}&gt; wrote:<blockquote style="margin:8px 0 0 4px;padding-left:12px;border-left:2px solid #c8c6c4;color:#424242">${sanitize(m.body, false).html}</blockquote></div>`;

let nidCount = 0;
const nid = () => `d${Date.now().toString(36)}${++nidCount}`;

const RECENT_KEY = "cilpron.mail.recent";
const PREF_KEY = "cilpron.mail.prefs";
const readJson = <T,>(key: string, fallback: T): T => {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : fallback;
  } catch {
    return fallback;
  }
};
const writeJson = (key: string, v: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(v));
  } catch {
    /* private mode */
  }
};

/* =====================================================================
   Small pieces
   ===================================================================== */
const Avatar: React.FC<{ name: string; size?: number }> = ({ name, size = 44 }) => (
  <span className={styles.avatar} style={{ width: size, height: size, background: `${colorOf(name)}22`, color: colorOf(name), fontSize: size * 0.36 }}>
    {initialsOf(name)}
  </span>
);

interface RowProps {
  mail: Mail;
  selected: boolean;
  selecting: boolean;
  previews: boolean;
  inTrash: boolean;
  onOpen: () => void;
  onSelect: () => void;
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
}

/** One message. Swipe right to archive, swipe left to delete (restore, in Deleted). Press and hold to select. */
const MailRow: React.FC<RowProps> = ({ mail, selected, selecting, previews, inTrash, onOpen, onSelect, onSwipeLeft, onSwipeRight }) => {
  const [dx, setDx] = useState(0);
  const start = useRef<{ x: number; y: number; t: number } | null>(null);
  const moved = useRef(false);
  const hold = useRef<number | undefined>(undefined);
  const axis = useRef<"" | "x" | "y">("");

  const clearHold = () => window.clearTimeout(hold.current);
  const down = (e: React.PointerEvent) => {
    start.current = { x: e.clientX, y: e.clientY, t: Date.now() };
    moved.current = false;
    axis.current = "";
    clearHold();
    hold.current = window.setTimeout(() => {
      if (!moved.current) {
        moved.current = true;
        if (navigator.vibrate) navigator.vibrate(12);
        onSelect();
      }
    }, 480);
  };
  const move = (e: React.PointerEvent) => {
    const s = start.current;
    if (!s || selecting) return;
    const mx = e.clientX - s.x;
    const my = e.clientY - s.y;
    if (!axis.current && (Math.abs(mx) > 8 || Math.abs(my) > 8)) {
      axis.current = Math.abs(mx) > Math.abs(my) ? "x" : "y";
      moved.current = true;
      clearHold();
    }
    if (axis.current === "x") setDx(Math.max(-140, Math.min(140, mx)));
  };
  const up = () => {
    clearHold();
    const was = dx;
    setDx(0);
    start.current = null;
    if (axis.current === "x") {
      if (was > 90) onSwipeRight();
      else if (was < -90) onSwipeLeft();
      return;
    }
    if (!moved.current) onOpen();
  };
  const cancel = () => {
    clearHold();
    setDx(0);
    start.current = null;
  };

  const name = mail.fromName || mail.from;
  const unread = !mail.isRead;
  return (
    <li className={styles.rowWrap}>
      <div className={`${styles.swipeBg} ${styles.swipeRight}`} style={{ opacity: dx > 8 ? 1 : 0 }}>
        <Archive24Regular /> <span>Archive</span>
      </div>
      <div className={`${styles.swipeBg} ${styles.swipeLeft}`} style={{ opacity: dx < -8 ? 1 : 0 }}>
        <span>{inTrash ? "Restore" : "Delete"}</span> {inTrash ? <FolderArrowRight24Regular /> : <Delete24Regular />}
      </div>
      <div
        role="button"
        tabIndex={0}
        className={`${styles.row} ${selected ? styles.rowSel : ""}`}
        style={{ transform: `translateX(${dx}px)`, transition: dx === 0 ? "transform 0.2s ease" : "none" }}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={cancel}
        onPointerLeave={() => start.current && axis.current !== "x" && cancel()}
        onContextMenu={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if (e.key === "Enter") onOpen();
        }}
      >
        <span className={styles.rowLead}>
          {selected ? (
            <span className={styles.tick}>
              <Checkmark24Regular />
            </span>
          ) : (
            <Avatar name={name} />
          )}
        </span>
        <span className={styles.rowBody}>
          <span className={styles.rowTop}>
            <span className={`${styles.rowFrom} ${unread ? styles.bold : ""}`}>{name}</span>
            <span className={`${styles.rowTime} ${unread ? styles.rowTimeNew : ""}`}>{listTime(mail.date)}</span>
          </span>
          <span className={styles.rowMid}>
            <span className={`${styles.rowSubject} ${unread ? styles.semi : ""}`}>{mail.subject || "(No subject)"}</span>
            <span className={styles.rowIcons}>
              {mail.pinned && <Pin16Filled className={styles.icoPin} />}
              {mail.attachments.length > 0 && <Attach16Regular className={styles.icoMuted} />}
              {mail.isFlagged && <Flag16Filled className={styles.icoFlag} />}
            </span>
          </span>
          {previews && <span className={styles.rowSnippet}>{mail.snippet || textOf(mail.body).slice(0, 140)}</span>}
        </span>
        {unread && <span className={styles.unreadDot} aria-label="Unread" />}
      </div>
    </li>
  );
};

/** Recipient chips with contact suggestions. */
const Recipients: React.FC<{ label: string; values: string[]; onChange: (v: string[]) => void; trailing?: React.ReactNode }> = ({ label, values, onChange, trailing }) => {
  const [text, setText] = useState("");
  const [sugg, setSugg] = useState<{ name: string; address: string }[]>([]);
  useEffect(() => {
    const t = text.trim();
    if (t.length < 2) {
      setSugg([]);
      return;
    }
    const ac = new AbortController();
    const timer = window.setTimeout(() => {
      mailApi
        .searchContacts(t, ac.signal)
        .then((r) => setSugg(r.map((c) => ({ name: c.name || nameFromAddr(c.email), address: c.email })).filter((c) => !values.includes(c.address)).slice(0, 5)))
        .catch(() => undefined);
    }, 200);
    return () => {
      window.clearTimeout(timer);
      ac.abort();
    };
  }, [text, values]);
  const commit = (raw: string) => {
    const parts = raw.split(/[;,\s]+/).map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    const next = [...values];
    parts.forEach((p) => {
      if (!next.includes(p)) next.push(p);
    });
    onChange(next);
    setText("");
    setSugg([]);
  };
  return (
    <div className={styles.rcpt}>
      <span className={styles.rcptLabel}>{label}</span>
      <div className={styles.rcptBox}>
        {values.map((v) => (
          <span key={v} className={`${styles.chip} ${isEmail(v) ? "" : styles.chipBad}`}>
            {nameFromAddr(v)}
            <button type="button" aria-label={`Remove ${v}`} onClick={() => onChange(values.filter((x) => x !== v))}>
              <Dismiss24Regular />
            </button>
          </span>
        ))}
        <input
          className={styles.rcptInput}
          type="email"
          inputMode="email"
          autoCapitalize="off"
          autoCorrect="off"
          value={text}
          onChange={(e) => {
            const v = e.target.value;
            if (/[;,\s]$/.test(v)) commit(v);
            else setText(v);
          }}
          onBlur={() => commit(text)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit(text);
            } else if (e.key === "Backspace" && !text && values.length) onChange(values.slice(0, -1));
          }}
          aria-label={label}
        />
      </div>
      {trailing}
      {sugg.length > 0 && (
        <ul className={styles.suggest}>
          {sugg.map((c) => (
            <li key={c.address}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => commit(c.address)}>
                <Avatar name={c.name} size={32} />
                <span>
                  <b>{c.name}</b>
                  <small>{c.address}</small>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* =====================================================================
   The app
   ===================================================================== */
export interface MobileMailProps {
  apiKey?: string | null;
  mailbox?: string | null;
  displayName?: string;
  /** Back to the app launcher. */
  onExit?: () => void;
}

export const MobileMailClient: React.FC<MobileMailProps> = ({ apiKey = null, mailbox = null, displayName, onExit }) => {
  const ME = (mailbox ?? "").trim().toLowerCase();
  const ME_NAME = displayName || nameFromAddr(ME);
  const SIG = `<br><div>Regards,<br>${ME_NAME}</div>`;
  useMemo(() => mailApi.configureMail(apiKey, ME || null), [apiKey, ME]);
  const connected = !!apiKey && !!ME;

  // A phone shows the page zoomed out (and everything tiny) when the page has no viewport tag, so make sure one is set while Mail is open.
  useEffect(() => {
    let m = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null;
    const created = !m;
    const prev = m ? m.getAttribute("content") : null;
    if (!m) {
      m = document.createElement("meta");
      m.name = "viewport";
      document.head.appendChild(m);
    }
    m.setAttribute("content", "width=device-width, initial-scale=1, viewport-fit=cover");
    return () => {
      if (!m) return;
      if (created) m.remove();
      else if (prev !== null) m.setAttribute("content", prev);
    };
  }, []);

  const [emails, setEmails] = useState<Mail[]>([]);
  const emailsRef = useRef<Mail[]>(emails);
  emailsRef.current = emails;
  const [apiFolders, setApiFolders] = useState<mailApi.ApiFolder[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [serverOthers, setServerOthers] = useState(0);
  const pageNo = useRef(1);

  const [tab, setTab] = useState<Tab>("mail");
  const [activeFolder, setActiveFolder] = useState("inbox");
  const [category, setCategory] = useState<"focused" | "other">("focused");
  const [filter, setFilter] = useState<FilterKey>("all");
  const [sort, setSort] = useState<SortKey>("dateDesc");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [recent, setRecent] = useState<string[]>(() => readJson<string[]>(RECENT_KEY, []));
  const [prefs, setPrefs] = useState(() => readJson(PREF_KEY, { focused: true, previews: true }));

  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [drawer, setDrawer] = useState(false);
  const [sheet, setSheet] = useState<null | "filter" | "move" | "newFolder" | "confirmDelete" | "more" | "reply">(null);
  const [toast, setToast] = useState<Toast>(null);
  const toastId = useRef(0);
  const toastTimer = useRef<number | undefined>(undefined);
  const [newFolder, setNewFolder] = useState("");

  // overlays that behave like pages (Android back / swipe-back closes them)
  const [readerId, setReaderId] = useState<string | null>(null);
  const [compose, setCompose] = useState<Draft | null>(null);
  const [stack, setStack] = useState<string[]>([]);
  const stackRef = useRef<string[]>([]);
  stackRef.current = stack;
  const [showImages, setShowImages] = useState<Set<string>>(new Set());
  const [infoOpen, setInfoOpen] = useState(false);
  const [acct, setAcct] = useState(false);
  const [activity, setActivity] = useState(false);
  const [actTab, setActTab] = useState<"notes" | "tips">("notes");
  const [favOpen, setFavOpen] = useState(true);
  const sending = useRef(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const sentinel = useRef<HTMLDivElement>(null);
  const pull = useRef<{ y: number; on: boolean }>({ y: 0, on: false });
  const [pullY, setPullY] = useState(0);

  useEffect(() => {
    const onPop = () => {
      const top = stackRef.current[stackRef.current.length - 1];
      setStack((s) => s.slice(0, -1));
      if (top === "compose") setCompose(null);
      if (top === "activity") setActivity(false);
      if (top === "reader") {
        setReaderId(null);
        setInfoOpen(false);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const pushPage = (name: "reader" | "compose" | "activity") => {
    window.history.pushState({ mx: name }, "");
    setStack((s) => [...s, name]);
  };
  const popPage = () => {
    if (stackRef.current.length) window.history.back();
  };
  const popAll = () => {
    const n = stackRef.current.length;
    if (n) window.history.go(-n);
  };

  const say = (msg: string, undo?: () => void) => {
    const id = ++toastId.current;
    setToast({ id, msg, undo: undo ?? null });
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast((t) => (t && t.id === id ? null : t)), undo ? 6500 : 3200);
  };
  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  const setAll = (next: Mail[]) => {
    emailsRef.current = next;
    setEmails(next);
  };
  const failed = (e: unknown) => {
    const code = e instanceof mailApi.ApiError ? e.code : "";
    say(code === "unauthorized" || code === "mailbox_not_in_tenant" || code === "mailbox_not_found" ? "Your access to this mailbox was refused." : "That didn’t go through. Try again.");
  };

  /* ---------- loading ---------- */
  const refreshFoldersNow = useCallback(() => {
    if (!connected) return;
    mailApi.getFolders().then(setApiFolders).catch(() => undefined);
  }, [connected]);

  const P = useRef({ activeFolder, filter, sort, q: debounced });
  P.current = { activeFolder, filter, sort, q: debounced };

  const mergeList = (folder: string, msgs: mailApi.ApiMail[], append: boolean) => {
    const cur = emailsRef.current;
    const old = new Map(cur.map((m) => [m.id, m] as const));
    const mapped = msgs.map((x) => ({ ...x, folder, attachments: x.attachments as Att[], body: x.body || old.get(x.id)?.body || "" }) as Mail);
    const ids = new Set(mapped.map((m) => m.id));
    setAll([...cur.filter((m) => (append || m.folder !== folder) && !ids.has(m.id)), ...mapped]);
  };
  const fetchPage = (page: number, signal?: AbortSignal) => {
    const p = P.current;
    return mailApi.listMessages({ folder: p.activeFolder, filter: p.filter, sort: p.sort, q: p.q || undefined, page, pageSize: PAGE_SIZE }, signal);
  };
  const reloadNow = useCallback(async () => {
    if (!connected) return;
    const folder = P.current.activeFolder;
    try {
      const r = await fetchPage(1);
      if (P.current.activeFolder !== folder) return;
      mergeList(folder, r.messages, false);
      setHasMore(r.hasMore);
      setServerOthers(r.othersUnread);
      pageNo.current = 1;
    } catch {
      /* keep what is on screen */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);
  const loadMore = () => {
    const folder = P.current.activeFolder;
    const next = pageNo.current + 1;
    fetchPage(next)
      .then((r) => {
        if (P.current.activeFolder !== folder) return;
        mergeList(folder, r.messages, true);
        pageNo.current = next;
        setHasMore(r.hasMore);
      })
      .catch(() => say("Couldn’t load more messages."));
  };
  const loadBody = (id: string) => {
    mailApi
      .getMessage(id)
      .then((f) => {
        setAll(emailsRef.current.map((x) => (x.id !== id ? x : { ...x, body: f.body, attachments: f.attachments as Att[], to: f.to, toNames: f.toNames, cc: f.cc, ccNames: f.ccNames })));
      })
      .catch((e) => {
        if (!mailApi.isAbort(e)) say("Couldn’t open this message.");
      });
  };

  useEffect(() => {
    setAll([]);
    setApiFolders([]);
    if (!connected) return;
    refreshFoldersNow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, ME]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!connected) {
      setLoading(false);
      return;
    }
    const ac = new AbortController();
    setLoading(true);
    setLoadError(null);
    pageNo.current = 1;
    fetchPage(1, ac.signal)
      .then((r) => {
        mergeList(activeFolder, r.messages, false);
        setHasMore(r.hasMore);
        setServerOthers(r.othersUnread);
        setLoading(false);
      })
      .catch((e) => {
        if (mailApi.isAbort(e)) return;
        setLoading(false);
        setLoadError(e instanceof mailApi.ApiError && (e.status === 401 || e.status === 403 || e.status === 404) ? "This mailbox can’t be opened with your current access." : "Couldn’t load messages. Check your connection.");
      });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, ME, activeFolder, filter, sort, debounced]);

  // new mail: check every minute while the app is visible
  useEffect(() => {
    if (!connected) return;
    const t = window.setInterval(() => {
      if (document.hidden) return;
      refreshFoldersNow();
      if (pageNo.current === 1) void reloadNow();
    }, 60000);
    return () => window.clearInterval(t);
  }, [connected, refreshFoldersNow, reloadNow]);

  // endless list
  useEffect(() => {
    const el = sentinel.current;
    const root = listRef.current;
    if (!el || !root || !hasMore) return;
    const io = new IntersectionObserver((es) => es.some((e) => e.isIntersecting) && !loading && loadMore(), { root, rootMargin: "240px" });
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loading, emails.length, activeFolder]);

  /* ---------- derived ---------- */
  const customFolders = useMemo(() => apiFolders.filter((f) => !f.isSystem && !f.isVirtual && !MAIL_FOLDERS.some((m) => m.id === f.id)).map((f) => ({ id: f.id, label: f.label })), [apiFolders]);
  const folderLabel = (id: string) => MAIL_FOLDERS.find((f) => f.id === id)?.label ?? customFolders.find((f) => f.id === id)?.label ?? id;
  const unreadOf = (id: string) => apiFolders.find((f) => f.id === id)?.unread ?? 0;

  const inFolder = useMemo(() => emails.filter((m) => m.folder === activeFolder && !(m.snoozeUntil && m.snoozeUntil > Date.now())), [emails, activeFolder]);
  const splitFocus = activeFolder === "inbox" && prefs.focused && tab === "mail" && !debounced;
  const othersUnread = splitFocus ? (hasMore ? serverOthers : inFolder.filter((m) => !m.focused && !m.isRead).length) : 0;
  const list = useMemo(() => {
    const pool = splitFocus ? inFolder.filter((m) => (category === "focused") === m.focused) : inFolder;
    const out = pool.filter((m) => {
      if (filter === "unread" && m.isRead) return false;
      if (filter === "flagged" && !m.isFlagged) return false;
      if (filter === "attachments" && m.attachments.length === 0) return false;
      return true;
    });
    const t = (m: Mail) => new Date(m.date).getTime();
    out.sort((a, b) => (a.pinned !== b.pinned ? Number(b.pinned) - Number(a.pinned) : sort === "dateAsc" ? t(a) - t(b) : t(b) - t(a)));
    return out;
  }, [inFolder, splitFocus, category, filter, sort]);

  const selecting = checked.size > 0;
  const targets = list.filter((m) => checked.has(m.id));
  const reading = readerId ? emails.find((m) => m.id === readerId) ?? null : null;
  const contacts = useMemo(() => {
    const map = new Map<string, { name: string; address: string; n: number }>();
    emails.forEach((m) => {
      const a = (m.from || "").toLowerCase();
      if (!a || a === ME) return;
      const e = map.get(a);
      if (e) e.n++;
      else map.set(a, { name: m.fromName || nameFromAddr(a), address: a, n: 1 });
    });
    return Array.from(map.values()).sort((x, y) => y.n - x.n).slice(0, 60);
  }, [emails, ME]);
  const unreadMail = useMemo(
    () => emails.filter((m) => m.folder === "inbox" && !m.isRead).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 30),
    [emails]
  );

  /* ---------- mutations ---------- */
  const commit = (next: Mail[], remote?: { api: () => Promise<unknown>; reload?: boolean }, msg?: string, undo?: () => void) => {
    const prev = emailsRef.current;
    setAll(next);
    if (msg) say(msg, undo);
    if (remote) {
      remote
        .api()
        .then(() => {
          refreshFoldersNow();
          if (remote.reload) void reloadNow();
        })
        .catch((e) => {
          setAll(prev);
          failed(e);
        });
    }
  };
  const patch = (ids: string[], fn: (m: Mail) => Mail, api: () => Promise<unknown>, msg?: string) => {
    const set = new Set(ids);
    commit(emailsRef.current.map((m) => (set.has(m.id) ? fn(m) : m)), { api }, msg);
  };

  const moveTo = (ids: string[], folder: string, label?: string) => {
    if (!ids.length) return;
    const origin = new Map<string, string>();
    emailsRef.current.forEach((m) => ids.includes(m.id) && origin.set(m.id, m.folder));
    const set = new Set(ids);
    const plural = ids.length === 1 ? "Conversation" : `${ids.length} conversations`;
    const back = () => {
      const byFolder = new Map<string, string[]>();
      origin.forEach((f, id) => byFolder.set(f, [...(byFolder.get(f) ?? []), id]));
      commit(emailsRef.current.map((m) => (set.has(m.id) ? { ...m, folder: origin.get(m.id) ?? m.folder } : m)), {
        api: () => Promise.all(Array.from(byFolder, ([f, l]) => mailApi.moveMessages(l, f))),
      });
    };
    commit(
      emailsRef.current.map((m) => (set.has(m.id) ? { ...m, folder, pinned: false, snoozeUntil: null } : m)),
      { api: () => mailApi.moveMessages(ids, folder) },
      label ?? `${plural} moved to ${folderLabel(folder)}`,
      back
    );
    setChecked(new Set());
  };
  const archive = (ids: string[]) => {
    if (!ids.length) return;
    moveTo(ids, "archive", ids.length === 1 ? "Archived" : `${ids.length} archived`);
  };
  const remove = (ids: string[]) => {
    if (!ids.length) return;
    if (activeFolder === "trash") {
      setSheet("confirmDelete");
      return;
    }
    moveTo(ids, "trash", ids.length === 1 ? "Moved to Deleted" : `${ids.length} moved to Deleted`);
  };
  const eraseForever = () => {
    const ids = reading ? [reading.id] : [...checked];
    const set = new Set(ids);
    commit(emailsRef.current.filter((m) => !set.has(m.id)), { api: () => mailApi.deleteMessages(ids) }, "Deleted for good");
    setChecked(new Set());
    setSheet(null);
    if (reading) popAll();
  };
  const toggleRead = (ids: string[]) => {
    const all = ids.every((id) => emailsRef.current.find((m) => m.id === id)?.isRead);
    patch(ids, (m) => ({ ...m, isRead: !all }), () => mailApi.setRead(ids, !all));
    setChecked(new Set());
  };
  const toggleFlag = (ids: string[]) => {
    const all = ids.every((id) => emailsRef.current.find((m) => m.id === id)?.isFlagged);
    patch(ids, (m) => ({ ...m, isFlagged: !all }), () => mailApi.setFlag(ids, !all));
    setChecked(new Set());
  };

  /* ---------- navigation ---------- */
  const goFolder = (id: string) => {
    setActiveFolder(id);
    setChecked(new Set());
    setSearch("");
    setFilter("all");
    setCategory("focused");
    setDrawer(false);
    setTab(id === "inbox" ? "mail" : "mail");
  };
  const openMail = (m: Mail) => {
    if (m.folder === "drafts") {
      void openDraft(m);
      return;
    }
    setReaderId(m.id);
    pushPage("reader");
    if (!m.isRead) patch([m.id], (x) => ({ ...x, isRead: true }), () => mailApi.setRead([m.id], true));
    if (!m.body) loadBody(m.id);
  };
  const closeReader = () => popPage();

  /* ---------- pull to refresh ---------- */
  const onTouchStart = (e: React.TouchEvent) => {
    if ((listRef.current?.scrollTop ?? 1) <= 0) pull.current = { y: e.touches[0].clientY, on: true };
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!pull.current.on) return;
    const d = e.touches[0].clientY - pull.current.y;
    if (d > 0) setPullY(Math.min(80, d * 0.5));
    else {
      pull.current.on = false;
      setPullY(0);
    }
  };
  const onTouchEnd = async () => {
    const was = pullY;
    pull.current.on = false;
    setPullY(0);
    if (was > 56 && !refreshing) {
      setRefreshing(true);
      refreshFoldersNow();
      await reloadNow();
      setRefreshing(false);
    }
  };

  /* ---------- compose ---------- */
  const ensureBody = async (m: Mail): Promise<Mail> => {
    if (m.body) return m;
    try {
      const f = await mailApi.getMessage(m.id);
      return { ...m, body: f.body, attachments: f.attachments as Att[], to: f.to, toNames: f.toNames, cc: f.cc, ccNames: f.ccNames };
    } catch {
      return m;
    }
  };
  const openCompose = async (mode: Draft["mode"], srcIn?: Mail, prefillTo?: string[]) => {
    const src = srcIn ? await ensureBody(srcIn) : undefined;
    let d: Draft = { key: nid(), draftId: null, mode, to: prefillTo ?? [], cc: [], bcc: [], subject: "", html: SIG, attachments: [] };
    if (src) {
      const pre = (re: RegExp, p: string) => (re.test(src.subject) ? src.subject : `${p}: ${src.subject}`);
      const toAddr = src.from === ME ? [...(src.to ?? [])] : [src.from];
      if (mode === "reply") d = { ...d, to: toAddr, subject: pre(/^re:/i, "Re"), html: SIG + quoteOf(src), sourceId: src.id };
      else if (mode === "replyAll")
        d = { ...d, to: Array.from(new Set([...toAddr, ...(src.to ?? []).filter((x) => x !== ME)])), cc: (src.cc ?? []).filter((x) => x !== ME), subject: pre(/^re:/i, "Re"), html: SIG + quoteOf(src), sourceId: src.id };
      else if (mode === "forward") d = { ...d, subject: pre(/^(fw|fwd):/i, "Fw"), html: SIG + quoteOf(src), attachments: [...src.attachments], sourceId: src.id, attachFrom: src.id };
    }
    setCompose(d);
    pushPage("compose");
  };
  const openDraft = async (m: Mail) => {
    try {
      const d = await mailApi.getDraft(m.id);
      setCompose({ key: nid(), draftId: d.draftId, mode: "new", to: d.to, cc: d.cc, bcc: d.bcc, subject: d.subject, html: d.html, attachments: d.attachments as Att[], attachFrom: d.draftId });
      pushPage("compose");
    } catch (e) {
      failed(e);
    }
  };
  const toPayload = async (d: Draft): Promise<mailApi.ComposePayload> => {
    const kept = d.attachments.filter((x) => x.id && !x.file);
    const fresh = d.attachments.filter((x) => x.file);
    return {
      draftId: d.draftId,
      mode: d.mode,
      to: d.to,
      cc: d.cc,
      bcc: d.bcc,
      subject: d.subject,
      html: d.html,
      inReplyTo: d.sourceId ?? null,
      attachmentsFrom: kept.length ? d.attachFrom ?? d.sourceId ?? d.draftId : null,
      keepAttachmentIds: kept.map((x) => x.id as string),
      attachments: await Promise.all(
        fresh.map(async (x) => ({
          name: x.name,
          ...(x.type && x.type !== "file" ? { contentType: x.type } : {}),
          contentBase64: await mailApi.fileToBase64(x.file as File),
        }))
      ),
    };
  };
  const saveDraft = async (d: Draft, quiet = false): Promise<string | null> => {
    try {
      const r = await mailApi.saveDraftApi(await toPayload(d));
      if (!quiet) say("Draft saved");
      refreshFoldersNow();
      if (P.current.activeFolder === "drafts") void reloadNow();
      return r.draftId;
    } catch (e) {
      if (!quiet) failed(e);
      return null;
    }
  };
  const doSend = async (d: Draft): Promise<boolean> => {
    if (sending.current) return false;
    if (d.to.length === 0 && d.cc.length === 0 && d.bcc.length === 0) {
      say("Add at least one recipient.");
      return false;
    }
    if (d.to.concat(d.cc, d.bcc).some((a) => !isEmail(a))) {
      say("One of the addresses isn’t valid.");
      return false;
    }
    sending.current = true;
    say("Sending…");
    try {
      await mailApi.sendMail(await toPayload(d));
      say("Message sent");
      refreshFoldersNow();
      if (P.current.activeFolder === "sent" || P.current.activeFolder === "drafts") void reloadNow();
      return true;
    } catch (e) {
      const code = e instanceof mailApi.ApiError ? e.code : "";
      say(code === "attachments_too_large" ? "Attachments are too large (25 MB limit)." : "Couldn’t send. Your message is still open.");
      return false;
    } finally {
      sending.current = false;
    }
  };
  const closeCompose = async (d: Draft | null, changed: boolean) => {
    if (d && changed) await saveDraft(d, false);
    popPage();
  };

  const createFolder = async () => {
    const name = newFolder.trim();
    if (!name) return;
    try {
      await mailApi.createFolder(name);
      setNewFolder("");
      setSheet(null);
      refreshFoldersNow();
      say(`Folder “${name}” created`);
    } catch (e) {
      say(e instanceof mailApi.ApiError && e.code === "exists" ? "A folder with that name already exists" : "Couldn’t create the folder. Try a different name.");
    }
  };

  const remember = (q: string) => {
    const t = q.trim();
    if (!t) return;
    const next = [t, ...recent.filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, 6);
    setRecent(next);
    writeJson(RECENT_KEY, next);
  };
  const setPref = (k: "focused" | "previews", v: boolean) => {
    const next = { ...prefs, [k]: v };
    setPrefs(next);
    writeJson(PREF_KEY, next);
  };

  /* ---------- reader body ---------- */
  const bodyHtml = useMemo(() => {
    if (!reading) return { html: "", blocked: 0 };
    return sanitize(reading.body || "", showImages.has(reading.id));
  }, [reading?.id, reading?.body, showImages]); // eslint-disable-line react-hooks/exhaustive-deps

  const nothingMsg = !connected
    ? "Your mailbox isn’t connected yet."
    : loadError
    ? loadError
    : debounced || filter !== "all"
    ? "No messages match."
    : `Nothing in ${folderLabel(activeFolder)}.`;

  const unreadInbox = unreadOf("inbox");
  const initials = initialsOf(ME_NAME);
  const openActivity = () => {
    setActivity(true);
    pushPage("activity");
  };
  const moveIds = readerId && stack.includes("reader") ? [readerId] : [...checked];

  const dRow = (key: string, id: string, label: string, icon: React.ReactElement) => (
    <li key={key}>
      <button type="button" className={`${styles.drawerRow} ${activeFolder === id ? styles.drawerOn2 : ""}`} onClick={() => goFolder(id)}>
        {icon}
        <span>{label}</span>
        {unreadOf(id) > 0 && <span className={styles.count}>{unreadOf(id)}</span>}
      </button>
    </li>
  );

  /* =====================================================================
     Render
     ===================================================================== */
  const listScreen = (
    <div className={styles.screen}>
      {tab === "search" ? (
        <header className={styles.searchBar}>
          <button type="button" className={styles.iconBtn} aria-label="Back" onClick={() => { setTab("mail"); setSearch(""); }}>
            <ArrowLeft24Regular />
          </button>
          <div className={styles.searchField}>
            <input
              autoFocus
              className={styles.searchInput}
              type="search"
              enterKeyHint="search"
              placeholder="Search mail and people"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && remember(search)}
            />
            {search && (
              <button type="button" className={styles.iconBtn} aria-label="Clear" onClick={() => setSearch("")}>
                <Dismiss24Regular />
              </button>
            )}
          </div>
        </header>
      ) : selecting ? (
        <header className={`${styles.appBar} ${styles.appBarSel}`}>
          <button type="button" className={styles.iconBtn} aria-label="Cancel selection" onClick={() => setChecked(new Set())}>
            <Dismiss24Regular />
          </button>
          <h1 className={styles.barTitle}>{checked.size} selected</h1>
          <button type="button" className={styles.iconBtn} aria-label="Mark read or unread" onClick={() => toggleRead([...checked])}>
            {targets.every((m) => m.isRead) ? <MailUnread24Regular /> : <MailRead24Regular />}
          </button>
          <button type="button" className={styles.iconBtn} aria-label="Flag" onClick={() => toggleFlag([...checked])}>
            <Flag24Regular />
          </button>
          <button type="button" className={styles.iconBtn} aria-label="Move" onClick={() => setSheet("move")}>
            <FolderArrowRight24Regular />
          </button>
          <button type="button" className={styles.iconBtn} aria-label="Delete" onClick={() => remove([...checked])}>
            <Delete24Regular />
          </button>
        </header>
      ) : (
        <header className={styles.appBar}>
          <button type="button" className={styles.iconBtn} aria-label="Folders" onClick={() => setDrawer(true)}>
            <Navigation24Regular />
          </button>
          <h1 className={styles.barTitle}>{folderLabel(activeFolder)}</h1>
          <button type="button" className={styles.iconBtn} aria-label="Activity" onClick={openActivity}>
            <Alert24Regular />
            {unreadInbox > 0 && <span className={styles.bellDot} />}
          </button>
          <button type="button" className={styles.iconBtn} aria-label="Search" onClick={() => setTab("search")}>
            <Search24Regular />
          </button>
          <button type="button" className={styles.avatarBtn} aria-label="Account" onClick={() => setAcct(true)}>
            <span className={styles.meAvatar}>{initials}</span>
          </button>
        </header>
      )}

      {!selecting && tab === "mail" && (
        <div className={styles.subBar}>
          {splitFocus ? (
            <div className={styles.seg} role="tablist">
              <button type="button" role="tab" aria-selected={category === "focused"} className={`${styles.segBtn} ${category === "focused" ? styles.segOn : ""}`} onClick={() => setCategory("focused")}>
                Focused
              </button>
              <button type="button" role="tab" aria-selected={category === "other"} className={`${styles.segBtn} ${category === "other" ? styles.segOn : ""}`} onClick={() => setCategory("other")}>
                Other{othersUnread > 0 && <span className={styles.segBadge}>{othersUnread}</span>}
              </button>
            </div>
          ) : (
            <span className={styles.subLabel}>
              {list.length}
              {hasMore ? "+" : ""} message{list.length === 1 ? "" : "s"}
            </span>
          )}
          <button type="button" className={`${styles.filterBtn} ${filter !== "all" || sort !== "dateDesc" ? styles.filterOn : ""}`} onClick={() => setSheet("filter")}>
            {filter === "all" ? "Filter" : filter === "attachments" ? "Attachments" : filter[0].toUpperCase() + filter.slice(1)}
          </button>
        </div>
      )}

      <div
        className={styles.listScroll}
        ref={listRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className={styles.pull} style={{ height: refreshing ? 44 : pullY }}>
          <span className={`${styles.pullIcon} ${refreshing ? styles.spin : ""}`} style={{ opacity: refreshing ? 1 : Math.min(1, pullY / 50) }}>
            <ArrowClockwise24Regular />
          </span>
        </div>

        {tab === "search" && !debounced && (
          <>
            {contacts.length > 0 && (
              <>
                <h3 className={styles.secTitle}>Suggested contacts</h3>
                <div className={styles.contactRow}>
                  {contacts.slice(0, 10).map((c) => {
                    const [first, ...rest] = c.name.split(" ");
                    return (
                      <button key={c.address} type="button" className={styles.contactBtn} onClick={() => setSearch(c.address)}>
                        <Avatar name={c.name} size={60} />
                        <span>{first}</span>
                        <small>{rest.join(" ") || " "}</small>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            <h3 className={styles.secTitle}>Recent keywords</h3>
            {recent.length > 0 ? (
              <ul className={styles.recent}>
                {recent.map((r) => (
                  <li key={r}>
                    <button type="button" onClick={() => setSearch(r)}>
                      <History24Regular /> {r}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className={styles.hint}>Your recent searches will show up here.</p>
            )}
          </>
        )}

        {(tab !== "search" || debounced) && (
          <>
            {loading && list.length === 0 ? (
              <ul className={styles.list} aria-busy="true">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <li key={i} className={styles.skel}>
                    <span className={styles.skelAv} />
                    <span className={styles.skelLines}>
                      <span />
                      <span />
                      <span />
                    </span>
                  </li>
                ))}
              </ul>
            ) : list.length === 0 ? (
              <div className={styles.empty}>
                <span className={styles.emptyIcon}>
                  <Mail24Regular />
                </span>
                <strong>{nothingMsg}</strong>
                {connected && !loadError && filter === "all" && !debounced && <span>New messages will show up here.</span>}
                {loadError && (
                  <button type="button" className={styles.linkBtn} onClick={() => void reloadNow()}>
                    Try again
                  </button>
                )}
              </div>
            ) : (
              <ul className={styles.list}>
                {list.map((m) => (
                  <MailRow
                    key={m.id}
                    mail={m}
                    selected={checked.has(m.id)}
                    selecting={selecting}
                    previews={prefs.previews}
                    inTrash={activeFolder === "trash"}
                    onOpen={() => {
                      if (selecting) setChecked((s) => {
                        const n = new Set(s);
                        if (n.has(m.id)) n.delete(m.id);
                        else n.add(m.id);
                        return n;
                      });
                      else {
                        if (tab === "search") remember(search);
                        openMail(m);
                      }
                    }}
                    onSelect={() => setChecked((s) => new Set(s).add(m.id))}
                    onSwipeLeft={() => (activeFolder === "trash" ? moveTo([m.id], "inbox", "Restored to Inbox") : remove([m.id]))}
                    onSwipeRight={() => archive([m.id])}
                  />
                ))}
              </ul>
            )}
            <div ref={sentinel} className={styles.sentinel} />
            {hasMore && list.length > 0 && <div className={styles.more}>Loading more…</div>}
          </>
        )}
      </div>

      {tab === "mail" && !selecting && (
        <button type="button" className={styles.fab} onClick={() => void openCompose("new")} aria-label="New message">
          <Add24Regular />
        </button>
      )}
    </div>
  );

  const peopleScreen = (
    <div className={styles.screen}>
      <header className={styles.appBar}>
        <h1 className={`${styles.barTitle} ${styles.barTitleBig}`}>People</h1>
      </header>
      <div className={styles.listScroll}>
        {contacts.length === 0 ? (
          <div className={styles.empty}>
            <span className={styles.emptyIcon}>
              <People24Regular />
            </span>
            <strong>No contacts yet</strong>
            <span>People you exchange mail with will show up here.</span>
          </div>
        ) : (
          <>
            <p className={styles.hint}>People you hear from most</p>
            <ul className={styles.list}>
              {contacts.map((c) => (
                <li key={c.address}>
                  <button type="button" className={styles.person} onClick={() => void openCompose("new", undefined, [c.address])}>
                    <Avatar name={c.name} size={44} />
                    <span>
                      <b>{c.name}</b>
                      <small>{c.address}</small>
                    </span>
                    <Mail24Regular className={styles.chev} />
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );

  const meScreen = (
    <div className={styles.screen}>
      <header className={styles.appBar}>
        <button type="button" className={styles.iconBtn} aria-label="Back" onClick={() => setTab("mail")}>
          <ArrowLeft24Regular />
        </button>
        <h1 className={styles.barTitle}>Account</h1>
      </header>
      <div className={styles.listScroll}>
        <div className={styles.meCard}>
          <Avatar name={ME_NAME} size={64} />
          <div>
            <div className={styles.meName}>{ME_NAME}</div>
            <div className={styles.meMail}>{ME || "No mailbox"}</div>
          </div>
        </div>
        <div className={styles.groupLabel}>Inbox</div>
        <div className={styles.panel}>
          <label className={styles.toggleRow}>
            <span>
              <b>Focused Inbox</b>
              <small>Split your inbox into Focused and Other</small>
            </span>
            <input type="checkbox" className={styles.switch} checked={prefs.focused} onChange={(e) => setPref("focused", e.target.checked)} />
          </label>
          <label className={styles.toggleRow}>
            <span>
              <b>Message previews</b>
              <small>Show the first line under each subject</small>
            </span>
            <input type="checkbox" className={styles.switch} checked={prefs.previews} onChange={(e) => setPref("previews", e.target.checked)} />
          </label>
        </div>
        <div className={styles.groupLabel}>Gestures</div>
        <div className={styles.panel}>
          <div className={styles.infoRow}>Swipe right on a message to archive it.</div>
          <div className={styles.infoRow}>Swipe left to delete it.</div>
          <div className={styles.infoRow}>Press and hold to select several.</div>
        </div>
        {onExit && (
          <button type="button" className={styles.exitBtn} onClick={onExit}>
            <Grid24Regular /> All apps
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.mxRoot}>
      {tab === "me" ? meScreen : tab === "people" ? peopleScreen : listScreen}

      {!selecting && tab !== "search" && tab !== "me" && (
        <nav className={styles.tabBar} aria-label="Mail navigation">
          <button
            type="button"
            className={`${styles.tabBtn} ${tab === "mail" ? styles.tabOn : ""}`}
            aria-current={tab === "mail" ? "page" : undefined}
            onClick={() => {
              if (tab === "mail" && activeFolder !== "inbox") goFolder("inbox");
              else {
                setSearch("");
                setTab("mail");
              }
            }}
          >
            <span className={styles.tabIcon}>
              {tab === "mail" ? <Mail24Filled /> : <Mail24Regular />}
              {unreadInbox > 0 && <span className={styles.tabBadge}>{unreadInbox > 99 ? "99+" : unreadInbox}</span>}
            </span>
            <span>Email</span>
          </button>
          <button type="button" className={`${styles.tabBtn} ${tab === "people" ? styles.tabOn : ""}`} aria-current={tab === "people" ? "page" : undefined} onClick={() => setTab("people")}>
            <span className={styles.tabIcon}>{tab === "people" ? <People24Filled /> : <People24Regular />}</span>
            <span>People</span>
          </button>
          {onExit && (
            <button type="button" className={styles.tabBtn} onClick={onExit}>
              <span className={styles.tabIcon}>
                <Grid24Regular />
              </span>
              <span>All apps</span>
            </button>
          )}
        </nav>
      )}

      {/* folder drawer */}
      <div className={`${styles.scrim} ${drawer ? styles.scrimOn : ""}`} onClick={() => setDrawer(false)} />
      <aside className={`${styles.drawer} ${drawer ? styles.drawerOn : ""}`} aria-hidden={!drawer}>
        {onExit && (
          <button type="button" className={styles.drawerApps} onClick={() => { setDrawer(false); onExit(); }}>
            <Grid24Regular />
            <span>All apps</span>
          </button>
        )}
        <div className={styles.drawerScroll}>
          <div className={styles.drawerSec}>
            <span>Favorites</span>
            <button type="button" aria-label={favOpen ? "Collapse favorites" : "Expand favorites"} onClick={() => setFavOpen((v) => !v)}>
              {favOpen ? <ChevronUp24Regular /> : <ChevronDown24Regular />}
            </button>
          </div>
          {favOpen && (
            <ul className={styles.drawerList}>
              {(["inbox", "sent", "trash"] as const).map((id) => {
                const f = MAIL_FOLDERS.find((x) => x.id === id) as (typeof MAIL_FOLDERS)[number];
                return dRow(`fav-${id}`, id, f.label, f.icon);
              })}
            </ul>
          )}
          <div className={styles.drawerRule} />
          <div className={styles.drawerSec}>
            <span>Folders</span>
            <button type="button" aria-label="New folder" onClick={() => { setDrawer(false); setSheet("newFolder"); }}>
              <Edit24Regular />
            </button>
          </div>
          <ul className={styles.drawerList}>
            {MAIL_FOLDERS.map((f) => dRow(f.id, f.id, f.label, f.icon))}
            {customFolders.map((f) => dRow(f.id, f.id, f.label, <Folder24Regular />))}
          </ul>
        </div>
      </aside>

      {/* account */}
      <div className={`${styles.scrim} ${styles.scrimClear} ${acct ? styles.scrimOn : ""}`} onClick={() => setAcct(false)} />
      <div className={`${styles.acct} ${acct ? styles.acctOn : ""}`} aria-hidden={!acct}>
        <div className={styles.acctTop}>
          <span>Cilpron Mail</span>
          {onExit && (
            <button type="button" onClick={() => { setAcct(false); onExit(); }}>
              All apps
            </button>
          )}
        </div>
        <div className={styles.acctMe}>
          <span className={styles.bigAv}>{initials}</span>
          <div>
            <b>{ME_NAME}</b>
            <span>{ME || "No mailbox"}</span>
            <button type="button" className={styles.acctLink} onClick={() => { setAcct(false); setTab("me"); }}>
              View account
            </button>
          </div>
        </div>
      </div>

      {/* activity */}
      <section className={`${styles.page} ${activity && stack.includes("activity") ? styles.pageOn : ""}`} aria-hidden={!activity}>
        <header className={styles.readBar}>
          <button type="button" className={styles.iconBtn} aria-label="Back" onClick={popPage}>
            <ArrowLeft24Regular />
          </button>
          <h2 className={styles.composeTitle}>Activity</h2>
        </header>
        <div className={styles.segWrap}>
          <div className={`${styles.seg} ${styles.segFull}`} role="tablist">
            <button type="button" role="tab" aria-selected={actTab === "notes"} className={`${styles.segBtn} ${actTab === "notes" ? styles.segOn : ""}`} onClick={() => setActTab("notes")}>
              Notifications
            </button>
            <button type="button" role="tab" aria-selected={actTab === "tips"} className={`${styles.segBtn} ${actTab === "tips" ? styles.segOn : ""}`} onClick={() => setActTab("tips")}>
              Tips
            </button>
          </div>
        </div>
        <div className={`${styles.readScroll} ${styles.actBody}`}>
          {actTab === "notes" ? (
            unreadMail.length === 0 ? (
              <div className={styles.empty}>
                <span className={styles.emptyIcon}>
                  <Alert24Regular />
                </span>
                <strong>You’re all caught up</strong>
                <span>New mail will be listed here.</span>
              </div>
            ) : (
              unreadMail.map((m) => (
                <button key={m.id} type="button" className={styles.card} onClick={() => openMail(m)}>
                  <Avatar name={m.fromName || m.from} size={40} />
                  <span>
                    <b>{m.fromName || m.from}</b>
                    <span className={styles.cardSub}>{m.subject || "(No subject)"}</span>
                    <small>{listTime(m.date)}</small>
                  </span>
                </button>
              ))
            )
          ) : (
            <>
              <div className={styles.card}>
                <span className={styles.tipIcon}>
                  <Folder24Regular />
                </span>
                <span>
                  <b>
                    Manage your Inbox <i className={styles.newTag}>NEW</i>
                  </b>
                  <span className={styles.cardSub}>Create folders to sort your email.</span>
                  <button type="button" className={styles.tryNow} onClick={() => setSheet("newFolder")}>
                    Try now
                  </button>
                </span>
              </div>
              <div className={styles.card}>
                <span className={styles.tipIcon}>
                  <Archive24Regular />
                </span>
                <span>
                  <b>Clean up fast</b>
                  <span className={styles.cardSub}>Swipe right to archive, left to delete, or press and hold to select several messages.</span>
                </span>
              </div>
              <div className={styles.card}>
                <span className={styles.tipIcon}>
                  <MailInbox24Regular />
                </span>
                <span>
                  <b>Focused Inbox</b>
                  <span className={styles.cardSub}>Keep important mail on top. Turn it on or off in Account.</span>
                  <button type="button" className={styles.tryNow} onClick={() => { popAll(); setTab("me"); }}>
                    Open Account
                  </button>
                </span>
              </div>
            </>
          )}
        </div>
      </section>

      {/* reader */}
      <section className={`${styles.page} ${readerId && stack.includes("reader") ? styles.pageOn : ""}`} aria-hidden={!readerId}>
        {reading && (
          <>
            <header className={styles.readBar}>
              <button type="button" className={styles.iconBtn} aria-label="Back" onClick={closeReader}>
                <ArrowLeft24Regular />
              </button>
              <span className={styles.readSpacer} />
              <button type="button" className={styles.iconBtn} aria-label="More" onClick={() => setSheet("more")}>
                <MoreHorizontal24Regular />
              </button>
              <button type="button" className={styles.iconBtn} aria-label="Delete" onClick={() => { if (reading.folder === "trash") setSheet("confirmDelete"); else { remove([reading.id]); popAll(); } }}>
                <Delete24Regular />
              </button>
              <button type="button" className={styles.iconBtn} aria-label="Archive" onClick={() => { archive([reading.id]); popAll(); }}>
                <Archive24Regular />
              </button>
            </header>
            <div className={styles.readScroll}>
              <div className={styles.readHead}>
                <h2 className={styles.readSubject}>{reading.subject || "(No subject)"}</h2>
                <button type="button" className={styles.flagBtn} aria-label={reading.isFlagged ? "Unflag" : "Flag"} onClick={() => toggleFlag([reading.id])}>
                  {reading.isFlagged ? <Flag24Filled className={styles.icoFlag} /> : <Flag24Regular />}
                </button>
              </div>
              {(reading.external || (bodyHtml.blocked > 0 && !showImages.has(reading.id))) && (
                <div className={styles.banner}>
                  <Info24Regular />
                  <div>
                    {reading.external && <p>This message came from outside your organisation. Be careful with links and attachments.</p>}
                    {bodyHtml.blocked > 0 && !showImages.has(reading.id) && (
                      <p>
                        Pictures in this message are hidden to protect your privacy.{" "}
                        <button type="button" onClick={() => setShowImages((s) => new Set(s).add(reading.id))}>
                          Show blocked content
                        </button>
                      </p>
                    )}
                  </div>
                </div>
              )}
              <button type="button" className={styles.sender} onClick={() => setInfoOpen((v) => !v)} aria-expanded={infoOpen}>
                <Avatar name={reading.fromName || reading.from} size={44} />
                <span className={styles.senderText}>
                  <b>{reading.fromName || reading.from}</b>
                  <small>
                    {infoOpen ? reading.from : `To: ${reading.to.length ? (reading.to[0] === ME ? "me" : reading.toNames[0] || reading.to[0]) : "me"}${reading.to.length > 1 ? ` +${reading.to.length - 1}` : ""}`}
                  </small>
                  <small>{fullTime(reading.date)}</small>
                </span>
                <ChevronDown16Regular className={infoOpen ? styles.flip : ""} />
              </button>
              {infoOpen && (
                <dl className={styles.meta}>
                  <div><dt>From</dt><dd>{reading.fromName ? `${reading.fromName} <${reading.from}>` : reading.from}</dd></div>
                  <div><dt>To</dt><dd>{reading.to.join(", ") || "—"}</dd></div>
                  {reading.cc.length > 0 && <div><dt>Cc</dt><dd>{reading.cc.join(", ")}</dd></div>}
                  <div><dt>Date</dt><dd>{fullTime(reading.date)}</dd></div>
                </dl>
              )}
              {reading.attachments.length > 0 && (
                <div className={styles.atts}>
                  {reading.attachments.map((a, i) => (
                    <button
                      key={a.id ?? `${a.name}${i}`}
                      type="button"
                      className={styles.att}
                      onClick={() => a.id && mailApi.downloadAttachment(reading.id, a.id, a.name).catch(() => say(`Couldn’t download “${a.name}”.`))}
                    >
                      <Attach24Regular />
                      <span>
                        <b>{a.name}</b>
                        <small>{sizeLabel(a.size)}</small>
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <div className={styles.bodyWrap}>
                {reading.body ? (
                  <div ref={bodyRef} className={styles.body} dangerouslySetInnerHTML={{ __html: bodyHtml.html }} />
                ) : (
                  <div className={styles.bodyLoading}>
                    <span />
                    <span />
                    <span />
                  </div>
                )}
              </div>
            </div>
            <footer className={styles.replyBar}>
              <button type="button" className={styles.replyMenu} aria-label="Reply options" onClick={() => setSheet("reply")}>
                <ArrowReply24Regular />
                <ChevronDown16Regular />
              </button>
              <button type="button" className={styles.replyInput} onClick={() => void openCompose("reply", reading)}>
                Reply
              </button>
            </footer>
          </>
        )}
      </section>

      {/* composer */}
      <section className={`${styles.page} ${styles.pageTop} ${compose && stack.includes("compose") ? styles.pageOn : ""}`} aria-hidden={!compose}>
        {compose && (
          <Composer
            key={compose.key}
            draft={compose}
            me={ME}
            onClose={(d, save) => void closeCompose(d, save)}
            onSend={async (d) => {
              if (!d.subject.trim() && !window.confirm("Send without a subject?")) return;
              if (await doSend(d)) {
                if (d.draftId) mailApi.discardDraft(d.draftId).catch(() => undefined);
                popAll();
              }
            }}
            onSay={say}
          />
        )}
      </section>

      {/* sheets */}
      <div className={`${styles.scrim} ${sheet ? styles.scrimOn : ""}`} onClick={() => setSheet(null)} />
      <div className={`${styles.sheet} ${sheet ? styles.sheetOn : ""}`} role="dialog" aria-modal="true">
        <div className={styles.grab} />
        {sheet === "more" && reading && (
          <div className={styles.sheetList}>
            <button type="button" onClick={() => { toggleRead([reading.id]); setSheet(null); }}>
              {reading.isRead ? <MailUnread24Regular /> : <MailRead24Regular />}
              <span>{reading.isRead ? "Mark as unread" : "Mark as read"}</span>
            </button>
            <button type="button" onClick={() => { toggleFlag([reading.id]); setSheet(null); }}>
              {reading.isFlagged ? <Flag24Filled /> : <Flag24Regular />}
              <span>{reading.isFlagged ? "Remove flag" : "Flag"}</span>
            </button>
            <button type="button" onClick={() => setSheet("move")}>
              <FolderArrowRight24Regular />
              <span>Move to folder</span>
            </button>
            <button type="button" onClick={() => { moveTo([reading.id], "junk", "Reported as junk"); setSheet(null); popAll(); }}>
              <Prohibited24Regular />
              <span>Report junk</span>
            </button>
            <button type="button" onClick={() => { moveTo([reading.id], "junk", "Reported as phishing"); setSheet(null); popAll(); }}>
              <ShieldError24Regular />
              <span>Report phishing</span>
            </button>
          </div>
        )}
        {sheet === "reply" && reading && (
          <div className={styles.sheetList}>
            <button type="button" onClick={() => { setSheet(null); void openCompose("reply", reading); }}>
              <ArrowReply24Regular />
              <span>Reply</span>
            </button>
            <button type="button" onClick={() => { setSheet(null); void openCompose("replyAll", reading); }}>
              <ArrowReplyAll24Regular />
              <span>Reply all</span>
            </button>
            <button type="button" onClick={() => { setSheet(null); void openCompose("forward", reading); }}>
              <ArrowForward24Regular />
              <span>Forward</span>
            </button>
          </div>
        )}
        {sheet === "filter" && (
          <>
            <h3 className={styles.sheetTitle}>Filter and sort</h3>
            <div className={styles.optGroup}>
              {(
                [
                  ["all", "All messages"],
                  ["unread", "Unread"],
                  ["flagged", "Flagged"],
                  ["attachments", "With attachments"],
                ] as [FilterKey, string][]
              ).map(([k, l]) => (
                <button key={k} type="button" className={`${styles.opt} ${filter === k ? styles.optOn : ""}`} onClick={() => setFilter(k)}>
                  {l}
                  {filter === k && <Checkmark24Regular />}
                </button>
              ))}
            </div>
            <div className={styles.optLabel}>Sort by</div>
            <div className={styles.optGroup}>
              {(
                [
                  ["dateDesc", "Newest first"],
                  ["dateAsc", "Oldest first"],
                ] as [SortKey, string][]
              ).map(([k, l]) => (
                <button key={k} type="button" className={`${styles.opt} ${sort === k ? styles.optOn : ""}`} onClick={() => setSort(k)}>
                  {l}
                  {sort === k && <Checkmark24Regular />}
                </button>
              ))}
            </div>
            <button type="button" className={styles.primary} onClick={() => setSheet(null)}>Done</button>
          </>
        )}
        {sheet === "move" && (
          <>
            <h3 className={styles.sheetTitle}>Move to</h3>
            <div className={styles.optGroup}>
              {[...MAIL_FOLDERS.map((f) => ({ id: f.id, label: f.label })), ...customFolders]
                .filter((f) => f.id !== activeFolder)
                .map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    className={styles.opt}
                    onClick={() => {
                      const inReader = !!readerId && stack.includes("reader");
                      moveTo(moveIds, f.id);
                      setSheet(null);
                      if (inReader) popAll();
                    }}
                  >
                    {f.label}
                  </button>
                ))}
            </div>
          </>
        )}
        {sheet === "newFolder" && (
          <>
            <h3 className={styles.sheetTitle}>New folder</h3>
            <input className={styles.textField} placeholder="Folder name" value={newFolder} onChange={(e) => setNewFolder(e.target.value)} onKeyDown={(e) => e.key === "Enter" && void createFolder()} autoFocus />
            <button type="button" className={styles.primary} disabled={!newFolder.trim()} onClick={() => void createFolder()}>Create folder</button>
          </>
        )}
        {sheet === "confirmDelete" && (
          <>
            <h3 className={styles.sheetTitle}>Delete for good?</h3>
            <p className={styles.sheetText}>These messages will be removed from Deleted and can’t be brought back.</p>
            <button type="button" className={styles.danger} onClick={eraseForever}>Delete</button>
            <button type="button" className={styles.secondary} onClick={() => setSheet(null)}>Cancel</button>
          </>
        )}
      </div>

      {toast && (
        <div className={styles.toast} role="status">
          <span>{toast.msg}</span>
          {toast.undo && (
            <button type="button" onClick={() => { toast.undo?.(); setToast(null); }}>Undo</button>
          )}
        </div>
      )}
    </div>
  );
};

/* =====================================================================
   Composer
   ===================================================================== */
const Composer: React.FC<{
  draft: Draft;
  me: string;
  onClose: (d: Draft | null, save: boolean) => void;
  onSend: (d: Draft) => Promise<void>;
  onSay: (m: string) => void;
}> = ({ draft, me, onClose, onSend, onSay }) => {
  const [to, setTo] = useState(draft.to);
  const [cc, setCc] = useState(draft.cc);
  const [bcc, setBcc] = useState(draft.bcc);
  const [showCc, setShowCc] = useState(draft.cc.length > 0 || draft.bcc.length > 0);
  const [fmt, setFmt] = useState(false);
  const [subject, setSubject] = useState(draft.subject);
  const [atts, setAtts] = useState<Att[]>(draft.attachments);
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLInputElement>(null);

  // the body is edited in place so the signature and the quoted message stay intact
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.innerHTML = sanitize(draft.html, true).html;
      if (draft.mode === "reply" || draft.mode === "replyAll" || draft.mode === "forward") {
        const r = document.createRange();
        r.setStart(bodyRef.current, 0);
        r.collapse(true);
        const s = window.getSelection();
        s?.removeAllRanges();
        s?.addRange(r);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = (): Draft => ({ ...draft, to, cc, bcc, subject, html: bodyRef.current?.innerHTML ?? draft.html, attachments: atts });
  // only keep a draft when something was actually written or changed
  const changed = (): boolean => {
    const d = current();
    return (
      d.to.join() !== draft.to.join() ||
      d.cc.join() !== draft.cc.join() ||
      d.bcc.join() !== draft.bcc.join() ||
      d.subject !== draft.subject ||
      d.attachments.length !== draft.attachments.length ||
      textOf(d.html) !== textOf(draft.html)
    );
  };
  const attach = (files: FileList | null) => {
    if (!files) return;
    const add: Att[] = Array.from(files).map((f) => ({ name: f.name, size: f.size, type: f.type || "file", file: f }));
    const total = [...atts, ...add].reduce((n, a) => n + a.size, 0);
    if (total > 25 * 1048576) {
      onSay("Attachments are too large (25 MB limit).");
      return;
    }
    setAtts([...atts, ...add]);
  };
  const style = (cmd: string) => {
    bodyRef.current?.focus();
    document.execCommand(cmd);
  };

  const title = draft.mode === "reply" ? "Reply" : draft.mode === "replyAll" ? "Reply all" : draft.mode === "forward" ? "Forward" : "New message";

  return (
    <>
      <header className={styles.readBar}>
        <button type="button" className={styles.iconBtn} aria-label="Close" onClick={() => onClose(current(), changed())}>
          <Dismiss24Regular />
        </button>
        <h2 className={styles.composeTitle}>{title}</h2>
        <button
          type="button"
          className={styles.iconBtn}
          aria-label="Send"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            await onSend(current());
            setBusy(false);
          }}
        >
          <Send24Regular />
        </button>
      </header>
      <div className={styles.composeScroll}>
        <Recipients
          label="To:"
          values={to}
          onChange={setTo}
          trailing={
            <button type="button" className={styles.rcptChev} aria-label={showCc ? "Hide Cc and Bcc" : "Show Cc and Bcc"} onClick={() => setShowCc((v) => !v)}>
              {showCc ? <ChevronUp24Regular /> : <ChevronDown24Regular />}
            </button>
          }
        />
        {showCc && (
          <>
            <Recipients label="Cc:" values={cc} onChange={setCc} />
            <Recipients label="Bcc:" values={bcc} onChange={setBcc} />
            <div className={styles.fromRow}>
              <span className={styles.rcptLabel}>From:</span>
              <span className={styles.fromVal}>{me}</span>
            </div>
          </>
        )}
        <div className={styles.subjectRow}>
          <input className={styles.subjectInput} placeholder="Add a subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
        {atts.length > 0 && (
          <div className={styles.atts}>
            {atts.map((a, i) => (
              <span key={`${a.name}${i}`} className={styles.att}>
                <Attach24Regular />
                <span>
                  <b>{a.name}</b>
                  <small>{sizeLabel(a.size)}</small>
                </span>
                <button type="button" aria-label={`Remove ${a.name}`} onClick={() => setAtts(atts.filter((_, j) => j !== i))}>
                  <Dismiss24Regular />
                </button>
              </span>
            ))}
          </div>
        )}
        <div ref={bodyRef} className={styles.editor} contentEditable suppressContentEditableWarning role="textbox" aria-multiline="true" aria-label="Message" data-placeholder="Write your message" />
      </div>
      {fmt && (
        <div className={styles.fmtRow}>
          {(
            [
              ["bold", "Bold", <TextBold24Regular key="b" />],
              ["italic", "Italic", <TextItalic24Regular key="i" />],
              ["underline", "Underline", <TextUnderline24Regular key="u" />],
              ["insertUnorderedList", "Bulleted list", <TextBulletList24Regular key="l" />],
            ] as [string, string, React.ReactElement][]
          ).map(([cmd, label, icon]) => (
            <button key={cmd} type="button" aria-label={label} onMouseDown={(e) => e.preventDefault()} onClick={() => style(cmd)}>
              {icon}
            </button>
          ))}
        </div>
      )}
      <footer className={styles.composeBar}>
        <button type="button" onClick={() => fileRef.current?.click()}>
          <Attach24Regular />
          <span>Attach</span>
        </button>
        <button type="button" onClick={() => imgRef.current?.click()}>
          <Image24Regular />
          <span>Photo</span>
        </button>
        <button type="button" className={fmt ? styles.composeOn : ""} onClick={() => setFmt((v) => !v)}>
          <TextFont24Regular />
          <span>Format</span>
        </button>
        <input ref={fileRef} type="file" multiple hidden onChange={(e) => { attach(e.target.files); e.target.value = ""; }} />
        <input ref={imgRef} type="file" accept="image/*" multiple hidden onChange={(e) => { attach(e.target.files); e.target.value = ""; }} />
      </footer>
    </>
  );
};

/* =====================================================================
   Page: signs the user in to the mail API (same flow as the desktop page)
   ===================================================================== */
const GLOBAL_API = (baseUrl.auth ?? "https://global.cilpron.com").replace(/\/+$/, "").replace(/\/api$/i, "");
const STORAGE_API_KEY = "cilpron_api_key";

const MobileEmail: React.FC<{ onExit?: () => void }> = ({ onExit }) => {
  const { user } = useAuth();
  const [apiKey, setApiKey] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_API_KEY);
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!user?.token || !user?.userId) return;
    let alive = true;
    axios
      .get(`${GLOBAL_API}/api/AccessToken/User/${user.userId}`, { headers: { Authorization: `Bearer ${user.token}` } })
      .then((res) => {
        const list: any[] = res.data?.["$values"] ?? (Array.isArray(res.data) ? res.data : []);
        const active = list.find((t) => !t.is_revoked && t.token_type === "ApiKey");
        if (!alive) return;
        if (active?.token) {
          setApiKey(active.token);
          try {
            localStorage.setItem(STORAGE_API_KEY, active.token);
          } catch {
            /* private mode */
          }
        } else {
          setApiKey(null);
          try {
            localStorage.removeItem(STORAGE_API_KEY);
          } catch {
            /* private mode */
          }
        }
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [user?.token, user?.userId]);

  const name = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();
  return <MobileMailClient apiKey={apiKey} mailbox={user?.email ?? null} {...(name ? { displayName: name } : {})} {...(onExit ? { onExit } : {})} />;
};

export default MobileEmail;